import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { ShieldCheck, ShieldAlert, Loader2, Link2, FileDown, FileCheck2 } from "lucide-react";

const OUTCOME_COLOR = { APPROVED: "var(--moss)", REJECTED: "var(--accent)", EXPIRED: "#6B7280" };

export default function VoiAudit() {
  const [rows, setRows] = useState(null);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/voi/audit`).then((r) => setRows(r.data.entries)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const [exporting, setExporting] = useState(false);
  const [exportCheck, setExportCheck] = useState(null);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API_BASE}/voi/audit/export.pdf`, { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "voi-audit.pdf";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Signed PDF downloaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setExporting(false); }
  };
  const verifyExport = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { setExportCheck((await axios.post(`${API_BASE}/voi/audit/exports/verify`, fd)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const verify = async () => {
    setChecking(true);
    try { setCheck((await axios.get(`${API_BASE}/voi/audit/verify`)).data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setChecking(false); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="voi-audit-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-[var(--ink)]">ID verification log</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1 max-w-xl">Append-only, hash-chained record of every identity check — kept 5 years under TASA. No ID images, videos or TFNs are stored.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" onClick={verify} disabled={checking} data-testid="voi-audit-verify">
            {checking ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Verify integrity
          </button>
          <label className="btn-secondary cursor-pointer" data-testid="voi-export-verify-label">
            <FileCheck2 size={14} /> Verify an export
            <input type="file" accept="application/pdf" className="hidden" data-testid="voi-export-verify-input"
                   onChange={(e) => { verifyExport(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          <button className="btn-primary" onClick={exportPdf} disabled={exporting} data-testid="voi-audit-export">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Download signed PDF
          </button>
        </div>
      </div>
      {check && (
        <div className="card p-4 mb-6 flex items-center gap-3" data-testid="voi-audit-verify-result"
             style={{ borderColor: check.ok ? "var(--moss)" : "var(--accent)" }}>
          {check.ok ? <ShieldCheck className="text-[var(--moss)]" /> : <ShieldAlert className="text-[var(--accent)]" />}
          <div className="text-[13px]">
            {check.ok ? <>Chain intact · {check.entries} entries · head <span className="font-mono">{check.head_hash?.slice(0, 16)}…</span></>
              : <>Tampering detected at entry #{check.broken_at_seq} ({check.audit_id})</>}
          </div>
        </div>
      )}
      {exportCheck && (
        <div className="card p-4 mb-6 flex items-center gap-3 text-[13px]" data-testid="voi-export-verify-result"
             style={{ borderColor: exportCheck.genuine ? "var(--moss)" : "var(--accent)" }}>
          {exportCheck.genuine ? <FileCheck2 className="text-[var(--moss)]" /> : <ShieldAlert className="text-[var(--accent)]" />}
          {exportCheck.genuine
            ? <span>Genuine, unaltered export {exportCheck.export_id} · generated {new Date(exportCheck.generated_at).toLocaleString()} · {exportCheck.entries} entries · chain {exportCheck.chain_ok ? "intact" : "BROKEN"} at export time</span>
            : <span>Not recognised — this PDF was not exported from your firm's ID verification log, or it has been modified.</span>}
        </div>
      )}
      {!rows ? <Loader2 className="animate-spin text-[var(--ink-3)]" /> : rows.length === 0 ? (
        <div className="text-[13px] text-[var(--ink-3)] py-10 text-center border border-dashed border-[var(--border)] rounded-md" data-testid="voi-audit-empty">No verifications yet.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="voi-audit-table">
            <thead className="text-left text-[var(--ink-3)] uppercase tracking-wider text-[10px]">
              <tr>{["#", "When", "Client", "Outcome", "Reviewer", "Agent no.", "ID sighted", "ATO data points", "Record hash"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {rows.map((r) => (
                <tr key={r.audit_id} data-testid={`voi-audit-row-${r.audit_id}`}>
                  <td className="px-3 py-2 font-mono">{r.seq}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(r.verified_timestamp).toLocaleString()}</td>
                  <td className="px-3 py-2">{r.client_name || r.client_ref_id}</td>
                  <td className="px-3 py-2 font-medium" style={{ color: OUTCOME_COLOR[r.outcome] }}>{r.outcome}{r.reason ? <div className="text-[var(--ink-3)] font-normal">{r.reason}</div> : null}</td>
                  <td className="px-3 py-2">{r.verified_by_name}</td>
                  <td className="px-3 py-2 font-mono">{r.verified_by_agent_number}</td>
                  <td className="px-3 py-2">{r.id_type_sighted.replace("_", " ")} · {r.id_country_state}</td>
                  <td className="px-3 py-2">{r.outcome === "APPROVED" ? `${r.ato_data_point_1}; ${r.ato_data_point_2}` : "—"}</td>
                  <td className="px-3 py-2 font-mono text-[var(--ink-3)]" title={r.record_hash}>{r.record_hash.slice(0, 12)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
